// Storm copy + taxonomy colors — pure, RN-portable. The single home for the
// calm-authority wording and the LOCKED color rules:
//   • Phase chrome:  watch = amber,  warning = red.
//   • Map polygon:   SVR = yellow,   every other warning = red.

import type { NwsAlert } from '@/lib/nws/types'
import type { WarningHazards } from './hazards'
import { isSvr } from './classify'

// Raw hex (for SVG fill/stroke, which won't take CSS var() attributes reliably).
export const HEX = {
  amber: '#ffd21a', // --status-watch (radar yellow)
  red: '#ff3131', // --status-severe (radar red)
  blue: '#3b82f6', // --status-quiet
} as const

/** CSS var to drive a screen's `--c` accent by phase. */
export const phaseAccentVar = (phase: 'watching' | 'storm') =>
  phase === 'storm' ? 'var(--status-severe)' : 'var(--status-watch)'

/** Warning-polygon color: SVR is yellow, everything else is red. */
export const polygonHex = (event: string) => (isSvr(event) ? HEX.amber : HEX.red)

/** "Severe Thunderstorm Warning" → "SEVERE THUNDERSTORM WARNING". */
export const warningTitle = (event: string) => event.toUpperCase()

export interface ShelterAction {
  headline: string
  body: string
}

/** Calm-authority protective action, chosen by the loudest hazard in the warning. */
export function shelterAction(alert: NwsAlert, hz: WarningHazards): ShelterAction {
  const event = alert.event.toLowerCase()

  if (/tornado/.test(event) || hz.tornado === 'OBSERVED') {
    return {
      headline: 'Take shelter now',
      body: 'Move to a basement or an interior room on the lowest floor — away from windows. Cover your head. If outdoors or in a vehicle, get into a sturdy building.',
    }
  }
  if (/flash flood/.test(event)) {
    return {
      headline: 'Move to higher ground',
      body: 'Get away from streams, creeks, and low spots now. Never walk or drive through flood water — turn around, don’t drown.',
    }
  }
  if (/snow squall/.test(event)) {
    return {
      headline: 'Avoid travel',
      body: 'Whiteout conditions and ice can hit in seconds. If you’re driving, reduce speed and increase following distance, or delay your trip until it passes.',
    }
  }
  // Severe thunderstorm (and the generic fallback).
  return {
    headline: 'Move indoors',
    body: 'Get inside a sturdy building and stay away from windows. Secure loose objects outside and avoid trees and power lines until the storm passes.',
  }
}

export interface WatchGuidance {
  /** One calm sentence on what the watch means. */
  meaning: string
  /** A short, scannable "what to watch for" list. */
  watchFor: string[]
}

/** Calm "what it means / what to watch for" for the amber WATCHING screen. */
export function watchGuidance(alert: NwsAlert): WatchGuidance {
  const event = alert.event.toLowerCase()

  if (/tornado/.test(event)) {
    return {
      meaning:
        'Conditions are favorable for tornadoes and severe storms in and near your area. A watch is not a warning — there’s no immediate threat yet, but now is the time to plan.',
      watchFor: [
        'Know where you’d shelter — lowest floor, interior room, away from windows.',
        'Keep your phone charged and notifications on.',
        'Rotating clouds, a dark or greenish sky, or a loud, persistent roar.',
      ],
    }
  }
  // Severe thunderstorm watch (and fallback).
  return {
    meaning:
      'Conditions are favorable for severe thunderstorms — damaging wind and large hail are possible in and near your area. A watch is not a warning; there’s no immediate threat yet.',
    watchFor: [
      'Bring in or secure loose outdoor items now.',
      'Keep your phone charged and notifications on.',
      'Fast-darkening skies, rising wind, distant thunder, or sudden gusts.',
    ],
  }
}

/** Short hazard chips for the WATCHING screen ("Damaging wind", "Large hail", …). */
export function watchHazardChips(alert: NwsAlert): string[] {
  const event = alert.event.toLowerCase()
  if (/tornado/.test(event)) return ['Tornadoes', 'Damaging wind', 'Large hail']
  return ['Damaging wind', 'Large hail', 'Frequent lightning']
}
